import React, { useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const MINIMAP_WIDTH = 200;
const MINIMAP_HEIGHT = 140;

/**
 * Overview panel showing the whole diagram with a draggable rectangle marking
 * the visible region. Reads live view state from the viewport ref each frame
 * rather than through props, so panning stays off the React render path.
 */
const DiagramMinimap = ({ viewportRef, mermaidCode, revision, onNavigate }) => {
  const hostRef = useRef(null);
  const rectRef = useRef(null);
  const frameRef = useRef(null);
  const layoutRef = useRef(null);
  const draggingRef = useRef(false);

  // Clone the rendered SVG into the minimap once the viewport reports that a
  // render finished. `revision` is what makes this race-free: waiting a fixed
  // delay instead would miss diagrams that Mermaid takes longer to lay out.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    host.innerHTML = '';
    if (!mermaidCode) return undefined;

    const svg = viewportRef.current?.getSvgElement();
    if (!svg) return undefined;

    const clone = svg.cloneNode(true);
    clone.removeAttribute('width');
    clone.removeAttribute('height');
    clone.style.width = '100%';
    clone.style.height = '100%';
    clone.style.maxWidth = 'none';
    clone.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Mermaid prefixes every id it emits - the root, the scoped <style>
    // selectors and the arrow markers - with the diagram id. Rewriting that
    // one string keeps the clone self-consistent instead of leaving two
    // elements in the document sharing an id.
    if (svg.id) {
      const uniqueId = `${svg.id}-minimap`;
      hostRef.current.innerHTML = clone.outerHTML.split(svg.id).join(uniqueId);
    } else {
      hostRef.current.innerHTML = '';
      hostRef.current.appendChild(clone);
    }
    return undefined;
  }, [mermaidCode, revision, viewportRef]);

  // Keep the viewport rectangle in sync with the live transform. This runs for
  // as long as there is a diagram and simply skips frames where the viewport
  // API is not available, rather than being gated on a flag that could latch.
  useEffect(() => {
    if (!mermaidCode) return undefined;

    let lastView = null;

    const tick = () => {
      frameRef.current = requestAnimationFrame(tick);

      const api = viewportRef.current;
      const rectEl = rectRef.current;
      if (!api || !rectEl) return;

      const view = api.getViewState();

      // The diagram is static most of the time. Bailing out on an unchanged
      // view keeps this loop from forcing a layout every single frame just to
      // redraw a rectangle in the same place.
      if (
        lastView &&
        lastView.x === view.x &&
        lastView.y === view.y &&
        lastView.zoom === view.zoom
      ) {
        return;
      }
      lastView = view;

      const size = api.getContentSize();
      const container = api.getContainer();
      const host = hostRef.current;
      if (!size || !container || !host) return;

      const scale = view.zoom / 100;
      const { x, y } = view;

      // Measure the host rather than using the nominal constants - the box has
      // a border, so its inner size is a couple of pixels smaller and the rect
      // would sit slightly off.
      const boxWidth = host.clientWidth;
      const boxHeight = host.clientHeight;

      const fit = Math.min(boxWidth / size.width, boxHeight / size.height);
      const offsetX = (boxWidth - size.width * fit) / 2;
      const offsetY = (boxHeight - size.height * fit) / 2;
      layoutRef.current = { fit, offsetX, offsetY, size, container, scale };

      const left = offsetX + (-x / scale) * fit;
      const top = offsetY + (-y / scale) * fit;
      const width = (container.clientWidth / scale) * fit;
      const height = (container.clientHeight / scale) * fit;

      rectEl.style.left = `${left}px`;
      rectEl.style.top = `${top}px`;
      rectEl.style.width = `${width}px`;
      rectEl.style.height = `${height}px`;
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [mermaidCode, viewportRef]);

  // Move the viewport so the clicked minimap point ends up centred.
  const navigateTo = useCallback(
    (clientX, clientY) => {
      const api = viewportRef.current;
      const layout = layoutRef.current;
      const host = hostRef.current;
      if (!api || !layout || !host) return;

      const bounds = host.getBoundingClientRect();
      const px = clientX - bounds.left;
      const py = clientY - bounds.top;

      const contentX = (px - layout.offsetX) / layout.fit;
      const contentY = (py - layout.offsetY) / layout.fit;

      const view = api.getViewState();
      const scale = view.zoom / 100;
      const targetX = layout.container.clientWidth / 2 - contentX * scale;
      const targetY = layout.container.clientHeight / 2 - contentY * scale;

      api.panBy(targetX - view.x, targetY - view.y);
      if (onNavigate) onNavigate();
    },
    [viewportRef, onNavigate]
  );

  const handlePointerDown = (e) => {
    e.stopPropagation();
    draggingRef.current = true;
    // Capture is a nicety for dragging past the minimap's edge - if the browser
    // refuses it, navigation must still happen.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {
      // Pointer is not capturable; dragging outside the box just won't track.
    }
    navigateTo(e.clientX, e.clientY);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    e.stopPropagation();
    navigateTo(e.clientX, e.clientY);
  };

  const handlePointerUp = (e) => {
    draggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      // Already released.
    }
  };

  if (!mermaidCode) return null;

  return (
    <Box
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      sx={{
        position: 'absolute',
        right: 12,
        bottom: 12,
        width: MINIMAP_WIDTH,
        height: MINIMAP_HEIGHT,
        borderRadius: 1,
        border: (t) => `1px solid ${t.palette.divider}`,
        backgroundColor: (t) =>
          t.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        boxShadow: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        zIndex: 5,
        touchAction: 'none',
        display: { xs: 'none', sm: 'block' }
      }}
    >
      <Box ref={hostRef} sx={{ width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.85 }} />
      <Box
        ref={rectRef}
        sx={{
          position: 'absolute',
          border: (t) => `2px solid ${t.palette.primary.main}`,
          backgroundColor: (t) =>
            t.palette.mode === 'dark' ? 'rgba(216, 56, 247, 0.16)' : 'rgba(216, 56, 247, 0.12)',
          pointerEvents: 'none',
          borderRadius: '2px'
        }}
      />
    </Box>
  );
};

export default DiagramMinimap;
